import { isAdjacent, isIntegerCoordinate, isOnBoard } from "./geometry";
import { coordinate, type Coordinate, type GameState, type Move } from "./model";

export type MoveNotationParseResult =
  Readonly<{ ok: true; move: Move }> | Readonly<{ ok: false; message: string }>;

export function coordinateNotation(position: Coordinate): string {
  return `r${position.row}c${position.column}`;
}

export function moveNotation(move: Move): string {
  const prefix = move.kind === "normal" ? "N" : "P";
  return `${prefix}:${coordinateNotation(move.from)}>${coordinateNotation(move.to)}`;
}

export function parseMoveNotation(value: string): MoveNotationParseResult {
  const match = /^(N|P):r(-?\d+)c(-?\d+)>r(-?\d+)c(-?\d+)$/.exec(value);
  if (match === null) return Object.freeze({ ok: false, message: "Invalid move notation." });

  const from = coordinate(Number(match[2]), Number(match[3]));
  const to = coordinate(Number(match[4]), Number(match[5]));
  if (!isIntegerCoordinate(from) || !isIntegerCoordinate(to) || !isOnBoard(from)) {
    return Object.freeze({ ok: false, message: "Move coordinates are invalid." });
  }

  if (match[1] === "N") {
    if (!isOnBoard(to) || !isAdjacent(from, to)) {
      return Object.freeze({ ok: false, message: "Normal-move geometry is invalid." });
    }
    return Object.freeze({ ok: true, move: Object.freeze({ kind: "normal", from, to }) });
  }

  if (Math.abs(to.row - from.row) !== 1 || Math.abs(to.column - from.column) > 1) {
    return Object.freeze({ ok: false, message: "Passage-move geometry is invalid." });
  }
  return Object.freeze({ ok: true, move: Object.freeze({ kind: "passage", from, to }) });
}

function outcomeNotation(state: GameState): string {
  if (state.outcome === null) return "-";
  const winner = state.outcome.winner === "black" ? "b" : "r";
  return state.outcome.kind === "passage" ? `p${winner}` : `n${winner}`;
}

function positionCellCode(state: GameState, index: number): number {
  const cell = state.cells[index];
  if (cell?.kind !== "field") return 0;
  let encoded = 1 << cell.stack.length;
  for (let stackIndex = 0; stackIndex < cell.stack.length; stackIndex += 1) {
    if (cell.stack[stackIndex] === "red") encoded |= 1 << stackIndex;
  }
  return encoded;
}

/** Exact repetition identity: field topology, stack order, and player to move. */
export function positionKey(state: GameState): string {
  const codes = state.cells.map((_, index) => positionCellCode(state, index));
  codes.push(state.activePlayer === "black" ? 0 : 1);
  return String.fromCharCode(...codes);
}

/** A deterministic rule-state key for fixtures, bot search, and diagnostics. */
export function stateNotation(state: GameState): string {
  const cells = state.cells
    .map((cell) => {
      if (cell.kind === "removed") return "x";
      if (cell.stack.length === 0) return "_";
      return cell.stack.map((player) => (player === "black" ? "b" : "r")).join("");
    })
    .join("/");
  return `v1|${state.activePlayer === "black" ? "b" : "r"}|${outcomeNotation(state)}|${cells}`;
}
